import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';
import { TalentRequestShowResponse, CustomTalentRequest, RecommendedContributor } from './types';

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  flex-wrap: wrap;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0 0 24px 0;
`;

const DeletedWarning = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 24px;
  font-size: 14px;
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
`;

const InfoTable = styled.table`
  width: 100%;
  max-width: 800px;
  border-collapse: collapse;
`;

const InfoRow = styled.tr``;

const InfoLabel = styled.td`
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  width: 200px;
  vertical-align: top;
`;

const InfoValue = styled.td`
  padding: 10px 12px;
  font-size: 14px;
  color: #374151;
  border: 1px solid #e5e7eb;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const Td = styled.td`
  padding: 10px 12px;
  font-size: 14px;
  color: #374151;
  border: 1px solid #e5e7eb;
  vertical-align: top;
`;

const ActionLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  font-size: 14px;
  &:hover { text-decoration: underline; }
`;

const NotifyButton = styled.button`
  color: #00a6c2;
  text-decoration: none;
  font-size: 14px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const ExternalLink = styled.a`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const BulletList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
  
  li {
    margin-bottom: 4px;
  }
`;

const EmptyRow = styled.div`
  padding: 16px;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 4px;
  text-align: left;
  margin-top: 8px;
`;

const InfoMessage = styled.div`
  padding: 12px 16px;
  background: #f3f4f6;
  border-radius: 4px;
  margin-top: 12px;
  font-size: 14px;
  color: #374151;
  
  ul {
    margin: 8px 0 0 0;
    padding-left: 20px;
  }
  
  li {
    margin-bottom: 4px;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const Divider = styled.div`
  border-top: 1px solid #e5e7eb;
  margin: 8px 0;
`;

const TopicsLabel = styled.div`
  font-weight: 600;
  margin-top: 8px;
`;

const formatDate = (dateStr?: string) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

function AdminPublicationTalentRequestShowPage() {
  const { id, talentRequestId } = useParams<{ id: string; talentRequestId: string }>();
  const [data, setData] = useState<TalentRequestShowResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin2/publications/${id}/talent_requests/${talentRequestId}.json`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load talent request', err);
      setError('Failed to load talent request');
    } finally {
      setLoading(false);
    }
  }, [id, talentRequestId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pubDisplayName = data?.publication 
    ? `${data.publication.account?.name} - ${data.publication.name}` 
    : 'Publication';

  const tr = data?.talentRequest;

  if (loading) {
    return (
      <AdminLayout 
        title="Talent Request" 
        breadcrumb={[
          { label: 'Publications', href: '/admin2/publications' },
          { label: 'Loading...' }
        ]}
      >
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  if (error || !tr) {
    return (
      <AdminLayout 
        title="Talent Request" 
        breadcrumb={[
          { label: 'Publications', href: '/admin2/publications' },
          { label: 'Error' }
        ]}
      >
        <Section>
          <EmptyRow>{error || 'Talent request not found'}</EmptyRow>
        </Section>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Talent Request" 
      breadcrumb={[
        { label: 'Publications', href: '/admin2/publications' },
        { label: pubDisplayName, href: `/admin2/publications/${id}/edit` },
        { label: 'Talent requests', href: `/admin2/publications/${id}/talent_requests` },
        { label: tr.title }
      ]}
    >
      <Breadcrumb>
        <Link to="/admin2/publications">Publications</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/edit`}>{pubDisplayName}</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/talent_requests`}>Talent requests</Link>
        <span>›</span>
        <span>{tr.title}</span>
      </Breadcrumb>

      <PageTitle>Talent Request</PageTitle>

      {tr.deletedAt && tr.deletedBy && (
        <DeletedWarning>
          The talent request you are viewing was deleted by {tr.deletedBy} on {formatDate(tr.deletedAt)}.
        </DeletedWarning>
      )}

      <Section>
        <InfoTable>
          <tbody>
            <InfoRow>
              <InfoLabel>Title</InfoLabel>
              <InfoValue>{tr.title}</InfoValue>
            </InfoRow>
            
            {!tr.defaultRequest && tr.description && (
              <InfoRow>
                <InfoLabel>Description</InfoLabel>
                <InfoValue>{tr.description}</InfoValue>
              </InfoRow>
            )}
            
            {tr.pillar && (
              <InfoRow>
                <InfoLabel>Pillar</InfoLabel>
                <InfoValue>{tr.pillar}</InfoValue>
              </InfoRow>
            )}
            
            {tr.storyFormat && (
              <InfoRow>
                <InfoLabel>Format</InfoLabel>
                <InfoValue>{tr.storyFormat}</InfoValue>
              </InfoRow>
            )}
            
            {tr.language && (
              <InfoRow>
                <InfoLabel>Language</InfoLabel>
                <InfoValue>{tr.language}</InfoValue>
              </InfoRow>
            )}
            
            {!tr.defaultRequest && (
              <>
                <InfoRow>
                  <InfoLabel>On site</InfoLabel>
                  <InfoValue>{tr.onSite ? tr.location : 'N/A'}</InfoValue>
                </InfoRow>
                
                {tr.otherInfo && (
                  <InfoRow>
                    <InfoLabel>Other info</InfoLabel>
                    <InfoValue>{tr.otherInfo}</InfoValue>
                  </InfoRow>
                )}
                
                {tr.contentExamples && tr.contentExamples.length > 0 && (
                  <InfoRow>
                    <InfoLabel>Content examples</InfoLabel>
                    <InfoValue>
                      <BulletList>
                        {tr.contentExamples.map((ex, idx) => (
                          <li key={idx}>
                            <ExternalLink href={ex} target="_blank" rel="noopener noreferrer">
                              {ex}
                            </ExternalLink>
                          </li>
                        ))}
                      </BulletList>
                      {tr.nlpTopics && tr.nlpTopics.length > 0 && (
                        <>
                          <Divider />
                          <TopicsLabel>Topics from content examples</TopicsLabel>
                          {tr.nlpTopics.join(', ')}
                        </>
                      )}
                    </InfoValue>
                  </InfoRow>
                )}
                
                <InfoRow>
                  <InfoLabel>Topics</InfoLabel>
                  <InfoValue>{tr.topics?.join(', ') || 'None'}</InfoValue>
                </InfoRow>
                
                <InfoRow>
                  <InfoLabel>Skills</InfoLabel>
                  <InfoValue>{tr.skills?.join(', ') || 'None'}</InfoValue>
                </InfoRow>
                
                <InfoRow>
                  <InfoLabel>Contributor pay</InfoLabel>
                  <InfoValue>
                    ${tr.budgetRangeMin || 0} - ${tr.budgetRangeMax || 0}
                  </InfoValue>
                </InfoRow>
                
                <InfoRow>
                  <InfoLabel>Needed by</InfoLabel>
                  <InfoValue>{formatDate(tr.neededBy)}</InfoValue>
                </InfoRow>
                
                <InfoRow>
                  <InfoLabel>Submitted</InfoLabel>
                  <InfoValue>{formatDate(tr.submittedAt)}</InfoValue>
                </InfoRow>
                
                {tr.creator && (
                  <InfoRow>
                    <InfoLabel>Creator</InfoLabel>
                    <InfoValue>{tr.creator}</InfoValue>
                  </InfoRow>
                )}
              </>
            )}
          </tbody>
        </InfoTable>
      </Section>

      {/* Custom Talent Requests Section */}
      <Section>
        <SectionTitle>Custom talent requests</SectionTitle>
        
        {tr.customTalentRequests && tr.customTalentRequests.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Submitted</Th>
                <Th>Recommended User Count</Th>
                <Th>Reasons</Th>
                <Th>Add. Info</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {tr.customTalentRequests.map((ctr: CustomTalentRequest) => (
                <tr key={ctr.id}>
                  <Td>{ctr.id}</Td>
                  <Td>{formatDate(ctr.submittedAt)}</Td>
                  <Td>{ctr.recommendedUserCount}</Td>
                  <Td>
                    {ctr.reasons && ctr.reasons.length > 0 ? (
                      <BulletList>
                        {ctr.reasons.map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </BulletList>
                    ) : (
                      'None'
                    )}
                  </Td>
                  <Td>{ctr.additionalInformation || 'N/A'}</Td>
                  <Td>
                    <ActionLink to={`/admin2/publications/${id}/talent_requests/${talentRequestId}/custom_talent_requests/${ctr.id}/recommended_contributors/new`}>
                      Add a recommended contributor
                    </ActionLink>
                    {' | '}
                    <NotifyButton 
                      onClick={async () => {
                        if (!confirm('Send notification to the talent request creator about newly recommended contributors?')) {
                          return;
                        }
                        try {
                          const res = await api.get(
                            `/admin2/publications/${id}/talent_requests/${talentRequestId}/custom_talent_requests/${ctr.id}/recommended_contributors/notify.json`
                          );
                          if (res.data.success) {
                            alert(res.data.message);
                            fetchData(); // Refresh the page
                          } else {
                            alert(res.data.message || 'Failed to send notification');
                          }
                        } catch (err: unknown) {
                          const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to send notification';
                          alert(errorMessage);
                        }
                      }}
                    >
                      Send notification
                    </NotifyButton>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyRow>No custom talent requests</EmptyRow>
        )}
      </Section>

      {/* Recommended Contributors Section */}
      <Section>
        <SectionTitle>Recommended contributors</SectionTitle>
        
        {data?.recommendedContributors && data.recommendedContributors.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Clips</Th>
                <Th>Status</Th>
                <Th>Manual</Th>
                <Th>Custom Talent Request ID</Th>
              </tr>
            </thead>
            <tbody>
              {data.recommendedContributors.map((rc: RecommendedContributor) => (
                <tr key={rc.id}>
                  <Td>{rc.name || 'Not found'}</Td>
                  <Td>{rc.email || 'Not found'}</Td>
                  <Td>
                    {rc.clips && rc.clips.length > 0 ? (
                      rc.clips.map((clip, idx) => (
                        <div key={idx}>{clip}</div>
                      ))
                    ) : (
                      ''
                    )}
                  </Td>
                  <Td>{rc.status}</Td>
                  <Td>{rc.manual ? 'true' : 'false'}</Td>
                  <Td>{rc.customTalentRequestId}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <>
            <EmptyRow>No recommended contributors</EmptyRow>
            {data?.enoughContent !== undefined && data?.contentStrategyCompleted !== undefined && (
              <InfoMessage>
                {data.enoughContent && data.contentStrategyCompleted ? (
                  'This publication has met all requirements, but recommendations have not yet been generated. Check back tomorrow.'
                ) : (
                  <ul>
                    {!data.contentStrategyCompleted && (
                      <li>This publication does not have a content strategy.</li>
                    )}
                    {!data.enoughContent && (
                      <li>This publication has not created enough english content.</li>
                    )}
                  </ul>
                )}
              </InfoMessage>
            )}
          </>
        )}
      </Section>
    </AdminLayout>
  );
}

export default AdminPublicationTalentRequestShowPage;
